import React, { useState } from "react";
import { Users } from "lucide-react";
import { COLORS } from "../constants";
import { CROWD_DENSITY_MIN, CROWD_DENSITY_MAX } from "../constants";
import { pill, fontSerif } from "./styles";

/**
 * NpcDensitySlider — manual override for the size of the NPC crowd.
 *
 *   - density === null  → "Auto" (day/night drives the count, 50–100)
 *   - density === number → exact requested count (25–150)
 *
 * Touching the slider switches to manual; clicking "Auto" returns to auto.
 * Persisted by `useCrowdState()` via localStorage.
 */
export default function NpcDensitySlider({ density, setDensity, inline = false }) {
  // Initial slider value when there's no manual override yet.
  const [lastManual, setLastManual] = useState(
    density === null ? 100 : density
  );
  const value = density === null ? lastManual : density;
  const isAuto = density === null;

  const onSlide = (e) => {
    const n = Number(e.target.value);
    setLastManual(n);
    setDensity(n);
  };

  const wrapperStyle = inline
    ? {
        display: "flex",
        alignItems: "center",
        gap: 10,
        ...pill,
        padding: "6px 12px",
        pointerEvents: "auto",
        color: COLORS.ivory,
        fontFamily: fontSerif,
        whiteSpace: "nowrap",
      }
    : {
        position: "absolute",
        top: 200,
        right: 24,
        display: "flex",
        alignItems: "center",
        gap: 10,
        ...pill,
        padding: "6px 12px",
        pointerEvents: "auto",
        color: COLORS.ivory,
        fontFamily: fontSerif,
      };

  return (
    <div data-testid="zone-density-slider" style={wrapperStyle}>
      <Users size={13} color={COLORS.gold} aria-hidden="true" />

      <input
        data-testid="zone-density-input"
        type="range"
        min={CROWD_DENSITY_MIN}
        max={CROWD_DENSITY_MAX}
        step={1}
        value={value}
        onChange={onSlide}
        aria-label="People density"
        style={{
          width: 110,
          accentColor: COLORS.gold,
          cursor: "pointer",
        }}
      />

      <span
        data-testid="zone-density-value"
        style={{
          fontSize: 11,
          letterSpacing: 1.5,
          fontWeight: 600,
          minWidth: 28,
          textAlign: "right",
          color: isAuto ? "rgba(243,234,212,0.55)" : COLORS.goldBright,
        }}
      >
        {value}
      </span>

      <button
        data-testid="zone-density-auto"
        onClick={() => setDensity(null)}
        aria-pressed={isAuto}
        title="Return to automatic day/night scaling"
        style={{
          padding: "3px 9px",
          borderRadius: 999,
          border: `1px solid ${isAuto ? COLORS.gold : COLORS.navyMid}`,
          background: isAuto ? COLORS.gold : "transparent",
          color: isAuto ? COLORS.navyDeep : COLORS.ivory,
          fontFamily: fontSerif,
          fontSize: 10,
          letterSpacing: 2,
          textTransform: "uppercase",
          fontWeight: 700,
          cursor: "pointer",
        }}
      >
        Auto
      </button>
    </div>
  );
}
